import { FocusableOption, FocusKeyManager } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { PointsSummaryFacade } from '@features/points-summary/facades/points-summary-facade/points-summary.facade';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { LoyaltyCurrencyFacade } from '@rd/core/facades/loyalty-currency-facade/loyalty-currency.facade';
import { EnvService } from '@rd/core/services/env/env.service';
import { GuardhouseService } from '@rd/core/services/guardhouse/guardhouse.service';
import { UrlUtilsService } from '@rd/core/services/url-utils/url-utils.service';
import { CORE_AUTH_SECURE_ROUTE_DIALOG } from '@rd/core/tokens/popup.token';
import { DialogService } from '@rd/popup/services/dialog/dialog.service';
import {
  GuardHouseLogoutCodes,
  LoyaltyCurrency,
  NavLink,
  PointsSummary,
} from '@rd/types';
import { isNotNull } from '@rd/utils/isNotNull';
import { combineLatest, Observable } from 'rxjs';
import { debounceTime, filter, map, tap } from 'rxjs/operators';
import { RewardsProgramSchemes } from '../../../rewards-program/types';
import { RewardsProgramsViewFacade } from '../../../rewards-program/views/facade/rewards-program-view.facade';
import {
  giftCardsPageList,
  offersPageList,
  PageUrlSegment,
  redeemCashPageList,
  transferProgramsPageList,
} from '../../../types/bnz-page-url-segment.type';
import { getMainUrlSegment } from '../../../utils/bnz-page-discovery.utils';
import { MyAccountLinkDirective } from '../../directives/my-account-link/my-account-link.directive';
import { MyRedemptionLinkDirective } from '../../directives/my-redemption-link/my-redemption-link.directive';
import { BnzNavLinksService } from '../../services/bnz-nav-links.service';
import {
  BNZ_BANKWIDE_UPDATES_ENABLED,
  BNZ_STAR_ICON_ENABLED,
} from '../../tokens/features.tokens';

@UntilDestroy()
@Component({
  selector: 'bnz-desktop-header',
  templateUrl: './desktop-header.component.html',
  styleUrls: ['./desktop-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BnzDesktopHeaderComponent implements AfterViewInit {
  @ViewChild('redemptionButton') redemptionButton: ElementRef;
  @ViewChild('myAccountButton') myAccountButton: ElementRef;
  @ViewChildren(MyRedemptionLinkDirective)
  redeemItems: QueryList<HTMLAnchorElement>;
  @ViewChildren(MyAccountLinkDirective)
  myAccountItems: QueryList<HTMLAnchorElement>;

  private coreAuthSecureRouteDialogComponentType = inject(
    CORE_AUTH_SECURE_ROUTE_DIALOG,
  );

  isStarIconEnabled = inject(BNZ_STAR_ICON_ENABLED);
  isBnzBankwideUpdatesEnabled = inject(BNZ_BANKWIDE_UPDATES_ENABLED);

  constructor(
    public bnzNavLinks: BnzNavLinksService,
    private router: Router,
    private envService: EnvService,
    private urlUtilService: UrlUtilsService,
    private pointsSummaryFacade: PointsSummaryFacade,
    private loyaltyCurrencyFacade: LoyaltyCurrencyFacade,
    private guardHouseService: GuardhouseService,
    private translateService: TranslateService,
    private dialogService: DialogService,
    private rewardsProgramsViewFacade: RewardsProgramsViewFacade,
    private cdRef: ChangeDetectorRef,
  ) {}

  isFlybuys$: Observable<boolean> =
    this.rewardsProgramsViewFacade.selectedScheme$.pipe(
      map((scheme) => scheme === RewardsProgramSchemes.Flybuys),
    );

  loyaltyCurrency$: Observable<LoyaltyCurrency> =
    this.loyaltyCurrencyFacade.getLoyaltyCurrencySelected$.pipe(
      filter(isNotNull),
    );

  pointsSummary$: Observable<PointsSummary> =
    this.pointsSummaryFacade.pointsSummary$.pipe(filter(isNotNull));

  pointsInformation$: Observable<[PointsSummary, LoyaltyCurrency]> =
    combineLatest([this.pointsSummary$, this.loyaltyCurrency$]).pipe(
      filter((values) => values.some(isNotNull)),
    );

  logoImage = this.urlUtilService.imageUrl('/images/branding/bnz.png');

  redeemKeyManager: FocusKeyManager<HTMLAnchorElement>;
  myAccountKeyManager: FocusKeyManager<HTMLAnchorElement>;

  redemptionExpanded = false;
  myAccountExpanded = false;

  loginUrl = this.guardHouseService.logoutUrl.toString();
  enrollmentUrl = this.guardHouseService.enrollUrl.toString();
  currentUrlSegment: PageUrlSegment;

  get isFlybuysLinkActive(): boolean {
    return (
      !this.router.url.includes('rewards_programs') &&
      this.router.url.includes('flybuys')
    );
  }

  get offerText(): string {
    return this.isBnzBankwideUpdatesEnabled
      ? 'wl.bnz.header.perks'
      : 'wl.bnz.header.offers';
  }

  toggleRedemption(): void {
    this.redemptionExpanded = !this.redemptionExpanded;
  }

  toggleMyAccount(): void {
    this.myAccountExpanded = !this.myAccountExpanded;
  }

  closeRedemption(): void {
    this.redemptionExpanded = false;
    this.redeemKeyManager.setActiveItem(-1);
  }

  closeMyAccount(): void {
    this.myAccountExpanded = false;
    this.myAccountKeyManager.setActiveItem(-1);
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (this.redemptionExpanded) {
      return this.redeemKeyManager?.onKeydown(event);
    }

    if (this.myAccountExpanded) {
      return this.myAccountKeyManager?.onKeydown(event);
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleEscapeKey(): void {
    if (this.redemptionExpanded) {
      this.closeRedemption();
      this.redemptionButton.nativeElement.focus();
      return;
    }

    if (this.myAccountExpanded) {
      this.closeMyAccount();
      this.myAccountButton.nativeElement.focus();
    }
  }

  ngAfterViewInit(): void {
    this.redeemItems.forEach(
      (el, id) =>
        (el.focus = () => document.getElementById(`redeem-${id}`)?.focus()),
    );

    this.myAccountItems.forEach(
      (el, id) =>
        (el.focus = () => document.getElementById(`myAccount-${id}`)?.focus()),
    );

    this.redeemKeyManager = new FocusKeyManager<HTMLAnchorElement>(
      this.redeemItems as QueryList<FocusableOption & HTMLAnchorElement>,
    )
      .withHorizontalOrientation('ltr')
      .withVerticalOrientation(true);

    this.myAccountKeyManager = new FocusKeyManager<HTMLAnchorElement>(
      this.myAccountItems as QueryList<FocusableOption & HTMLAnchorElement>,
    )
      .withHorizontalOrientation('ltr')
      .withVerticalOrientation(true);

    this.currentUrlSegment = getMainUrlSegment(this.router.url);
    this.router.events
      .pipe(
        untilDestroyed(this),
        tap((event) => {
          if (event instanceof NavigationEnd) {
            this.currentUrlSegment = getMainUrlSegment(event.urlAfterRedirects);
            this.cdRef.markForCheck();
          }
        }),
        filter(
          (event) =>
            event instanceof NavigationEnd &&
            !this.router.getCurrentNavigation()?.extras?.state
              ?.keepScrollPosition,
        ),
        // navigationEnd event happens when the old route still being displayed
        // add a delay to avoid live announcer say the old route h1 content
        debounceTime(500),
      )
      .subscribe((event) => {
        const heading = document.querySelector('h1');
        if (!heading) {
          return;
        }
        heading.classList.add('focus-link');
        heading.setAttribute('tabindex', '-1');
        heading.focus();
      });
  }

  openLoginModal(): void {
    if (this.envService.isDevelopment) {
      this.guardHouseService.redirectToLoginUrl();
    } else {
      this.dialogService.open(this.coreAuthSecureRouteDialogComponentType, {
        id: 'auth-secure-route-dialog',
        screenMode: 'center-md',
        ariaLabel: this.translateService.instant(
          'accessibility.core.auth_dialog_aria_label',
        ),
      });
    }
  }

  redirectToEnrollment(): void {
    this.guardHouseService.redirectToEnrollUrl();
  }

  redirectToLogout(): void {
    this.guardHouseService.redirectToLogoutUrl(
      GuardHouseLogoutCodes.IntentionalLogout,
    );
  }

  isExternalNavLink(navLink: NavLink): boolean {
    const navLinkString = navLink.route.toString();
    return (
      navLinkString.startsWith('http://') ||
      navLinkString.startsWith('https://')
    );
  }

  isActivatedRedeemMenuItem(): boolean {
    const redeemMenu = [
      ...giftCardsPageList,
      ...transferProgramsPageList,
      ...redeemCashPageList,
    ];
    return redeemMenu.includes(this.currentUrlSegment);
  }

  isActivatedOffersMenuItem(): boolean {
    return offersPageList.includes(this.currentUrlSegment);
  }

  isActivatedChildMenuItem(pageUrlSegment: string): boolean {
    switch (pageUrlSegment) {
      case PageUrlSegment.GIFTCARD_LIST: {
        return giftCardsPageList.includes(this.currentUrlSegment);
      }
      case PageUrlSegment.TRANSFER_PROGRAMS: {
        return (
          !this.router.url.includes('flybuys') &&
          transferProgramsPageList.includes(this.currentUrlSegment)
        );
      }
      case PageUrlSegment.REDEEMCASH_DETAILS: {
        return redeemCashPageList.includes(this.currentUrlSegment);
      }
      case PageUrlSegment.POINTS_TRANSACTIONS: {
        return this.currentUrlSegment === PageUrlSegment.POINTS_TRANSACTIONS;
      }
      case PageUrlSegment.HOMEPAGE: {
        return this.currentUrlSegment === PageUrlSegment.HOMEPAGE;
      }
      case PageUrlSegment.REWARDS_PROGRAMS: {
        return this.currentUrlSegment === PageUrlSegment.REWARDS_PROGRAMS;
      }
      case PageUrlSegment.CONTACT_US: {
        return this.currentUrlSegment === PageUrlSegment.CONTACT_US;
      }
      case PageUrlSegment.FAQ: {
        return this.currentUrlSegment === PageUrlSegment.FAQ;
      }
      case PageUrlSegment.SECURITY_AND_PRIVACY_STATEMENT: {
        return (
          this.currentUrlSegment ===
          PageUrlSegment.SECURITY_AND_PRIVACY_STATEMENT
        );
      }
      case PageUrlSegment.TERMS: {
        return this.currentUrlSegment === PageUrlSegment.TERMS;
      }
      case PageUrlSegment.AUTO_REDEMPTION_SETTINGS: {
        return (
          this.currentUrlSegment === PageUrlSegment.AUTO_REDEMPTION_SETTINGS
        );
      }
      case PageUrlSegment.SHARE_POINTS: {
        return this.currentUrlSegment === PageUrlSegment.SHARE_POINTS;
      }
    }
    return false;
  }
}
